import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { AuthService } from 'src/app/services/auth.service';
import appConfig from 'src/app/app-config';
import { uploadData } from 'aws-amplify/storage';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
})
export class FileUploadComponent implements OnInit {
  /**
   * Holds list of allowed file extensions
   */
  @Input() allowedExtensions: string[] = [];
  /**
   * Stores the text value for file upload label
   */
  @Input() labelText: string = 'Select file to upload';
  /**
   * Optional attribute to support/display hint labels
   */
  @Input() hintLabel?: string = '';
  /**
   * Indicates if component is being rendered for reporting issue
   */
  @Input() isReportIssue?: boolean;
  /**
   * Indicates user is restricted to perform operations in S3
   */
  @Input() restrictedUser?: boolean;
  /**
   * Event emitter for upload result
   */
  @Output() uploadResult: EventEmitter<any> = new EventEmitter();
  /**
   * Holds the details for the current user
   */
  userDetails: any;
  /**
   * Loader used to indicate file is loading
   */
  uploading: boolean = false;
  /**
   * List of the files that are in queue for getting uploaded
   */
  fileList: NzUploadFile[] = [];
  /**
   * Shows the upload progress of file
   */
  progressRatio: number = 0;
  /**
   * Indicates the loaded is spinning
   */
  isSpinning: boolean = false;
  private appConfig: any

  /**
   * Constructor for {@link FileUploadComponent}
   * @param msg an instance of {@link NzMessageService} used to show informational messages to users
   * @param authService an instance of {@link AuthService} used to get the logged in user info
   */
  constructor(private nzMessageService: NzMessageService, private authService: AuthService) { }

  ngOnInit(): void {
    this.getUserDetails();
  }

  async getUserDetails() {
    this.appConfig = appConfig;
    this.userDetails = await this.authService.getUserInfo();
    this.restrictedUser = this.userDetails?.['custom:isRestricted'] === 'true';
  }

  /**
   * Method to concat the file for upload before uploading to S3
   * @param file File to be added
   * @returns false
   */
  beforeUpload = (file: NzUploadFile): boolean => {
    this.fileList = this.fileList.concat(file);
    return false;
  };

  /***
   * Method to upload private file to S3
   */
  privateUpload(): void {
    this.fileUpload('private');
  }

  /***
   * Method to upload public file to S3
   */
  publicUpload(): void {
    this.fileUpload('public');
  }

  async fileUpload(access: 'private' | 'public'): Promise<void> {
    const folderPath = this.userDetails?.['custom:folderPath']?.trim();
    const bucket = this.userDetails?.['custom:bucketName']?.trim();
    
    if (!this.fileList.every((file) => this.validateFile(file))) {
      this.nzMessageService.warning('Some files are invalid or missing.');
      return;
    }

    this.uploading = true;
    this.isSpinning = true;
    
    try {
      for (const nzFile of this.fileList) {
        const file: File = (nzFile as any).originFileObj ?? (nzFile as any);
        
        let filePath = `${access}`;
        if (folderPath) {
          filePath = `${access}/${folderPath}`;
        }
        await uploadData({
          path: ({ identityId }) => `${filePath}/${identityId}/${file.name}`,
          data: file,
          options: {
            bucket: {
              bucketName: bucket,
              region: 'us-east-1'
            },
            onProgress: (progress: { transferredBytes: number; totalBytes: number; }) => {
              this.progressRatio = Math.round(
                (progress.transferredBytes / progress.totalBytes) * 100);
            },
          },
        }).result;
      }
      this.nzMessageService.success('File(s) upload successfully.');
      this.fileList = [];
    } catch (error: any) {
      this.nzMessageService.error(`File upload failed: ${error.message || error} `);
    } finally {
      this.uploading = false;
      this.isSpinning = false;
      this.progressRatio = 0;
    }
  }

  /**
   * Method to validated uploaded files are from allowed file list
   * @param file File Object
   * @returns Boolean true when file is valid and false for invalid
   */
  validateFile(file: NzUploadFile): boolean {
    const validFileTypes = [...this.allowedExtensions, 'text/csv'];
    const fileExtension = file.name.split('.')[1];
    if (!validFileTypes.includes(file.type || `.${fileExtension}`)) {
      this.nzMessageService.error(`${file.name} file is not valid, please select valid CSV file`);
      setTimeout(() => (this.isSpinning = false), 200);
      return false;
    }

    return true;
  }

  handleUpload() {
    if (this.restrictedUser) {
      this.nzMessageService.error('You are not authorized to perform operations on S3 bucket. Please contact admin');
      this.fileList = [];
      return;
    }
    if (this.isReportIssue) {
      this.publicUpload();
      return;
    }
    this.privateUpload();
  }
}
